/**
 * The photoreal render's settings — what the Render window sets and the room
 * export carries to Blender as `manifest.render` (scripts/blender/render_room.py
 * reads it; contract in scripts/blender/RENDER-SETTINGS.md).
 *
 * Every factory value is what the renderer did before these settings
 * existed, so a render with nothing changed looks as it did — except the
 * board joints, which follow the look's seam Strength (see `joints`).
 * Lengths are feet, angles degrees, unless a field says otherwise.
 */

export const PHOTO_RENDER_VERSION = 1;

/** The pictures: the designer's view; it and four from the room's corners; a high-res 360 only; the five and the 360. */
export type RenderViews = 'user' | 'all' | 'pano' | 'all+pano';
export const VIEW_SETS: RenderViews[] = ['user', 'all', 'pano', 'all+pano'];
/** The 360's widths (it is always 2:1): 8192 is crisp in a 1080p viewer window. */
export const PANO_WIDTHS = [4096, 6144, 8192] as const;
export const hasPanorama = (v: RenderViews): boolean => v === 'pano' || v === 'all+pano';
/** The picture's shape: the designer's view's own, or a fixed ratio (width : height). */
export type OutputAspect = 'view' | '16:9' | '16:10' | '3:2' | '4:3' | '5:4' | '1:1';
export const OUTPUT_ASPECTS: OutputAspect[] = ['view', '16:9', '16:10', '3:2', '4:3', '5:4', '1:1'];
export type SunMode = 'auto' | 'on' | 'off';
/** The camera's lens: a normal one, or a 180° fisheye. (A 360 is a picture of its own: output.views.) */
export type LensType = 'perspective' | 'fisheye';
export const LENS_TYPES: LensType[] = ['perspective', 'fisheye'];
/** The focal lengths offered as presets (mm, full-frame: 36 mm across the picture's longer side). */
export const FOCAL_PRESETS = [14, 24, 35, 50, 85, 135];
export type WorldMode = 'sky' | 'hdri';
export type ViewTransform = 'AgX' | 'Filmic' | 'Standard';
/** AgX's contrast looks (Blender: "AgX - <name>"); 'Base' is the renderer's own. */
export type ContrastLook = 'Very Low' | 'Low' | 'Medium Low' | 'Base' | 'Medium High' | 'High' | 'Very High' | 'Punchy';

/** A PVC surface: the Trusscore products. */
export interface PvcFinish {
  /** 0 mirror … 1 matte. */
  roughness: number;
  /** Specular IOR level: how strongly it reflects head-on (0 … 1; 0.5 is plastic's own). */
  specular: number;
  /** A clear coat over it: weight 0 … 1, and that coat's own roughness. */
  coat: number;
  coatRoughness: number;
}

export interface PhotoRenderSettings {
  version: number;
  output: {
    /** Cycles samples (adaptive): 16 … 4096. */
    samples: number;
    /** The denoiser: cleaner at low samples, a touch softer. */
    denoise: boolean;
    /** Image width in pixels. 640 … 7680. */
    width: number;
    /**
     * The picture's shape, with `width` giving its height: the designer's
     * view's own ('view'), or a fixed ratio. The camera keeps the view's
     * vertical field of view either way, so a wider ratio shows more at the
     * sides and a narrower one less — as resizing the designer's window does.
     */
    aspect: OutputAspect;
    /** The designer's view only, or it and four views from the room's corners. */
    views: RenderViews;
    /**
     * The 360's width (pixels; it is twice as wide as tall), when the
     * pictures include one: from the room's centre, level, its middle on the
     * wall staging and the openings say (panorama.ts). Depth of field, the
     * lens, the shape, vignette and colour fringing are left out of it.
     */
    panoWidth: (typeof PANO_WIDTHS)[number];
  };
  camera: {
    /**
     * The lens's focal length in mm, full-frame (36 mm across the picture's
     * longer side); null: the designer's view as it is. From the same spot, a
     * longer lens shows less (a 50 mm picture is the middle of a 16 mm one):
     * step the camera back to frame it. A fisheye takes it as its own focal
     * length (null: 15 mm, a full-frame fisheye).
     */
    focalMm: number | null;
    lensType: LensType;
    /** Depth of field: what is not at the focus distance blurs, as through a real lens. */
    dof: boolean;
    /** The aperture: smaller numbers, shallower focus. 0.95 … 22. */
    fStop: number;
    /** Feet from the camera to what is sharp; null: whatever is at the centre of the frame. */
    focusFt: number | null;
    /** The aperture's blades: 0 round, 3 … 12 a polygon — the shape of the out-of-focus highlights (bokeh). */
    bokehBlades: number;
    /** The blades' turn, degrees. */
    bokehRotation: number;
    /** Anamorphic bokeh: 1 round; above 1 tall ovals, below 1 wide. 0.5 … 2. */
    bokehRatio: number;
  };
  light: {
    /** Daylight through the biggest glass wall: auto = when the room has glass and no HDRI, or forced on / off. */
    sun: SunMode;
    /** The sun's strength (W/m²), its height above the horizon and its swing off the window's straight-in direction. */
    sunStrength: number;
    sunElevation: number;
    sunSwing: number;
    /** How soft its shadows are: the sun's apparent size, degrees. */
    sunSoftness: number;
    /** What lights the room from outside: a pale sky colour, or an HDRI. */
    world: WorldMode;
    skyStrength: number;
    /** An HDRI from the render library (scripts/blender/hdri), by file name. */
    hdri: string | null;
    hdriStrength: number;
    /** The HDRI turned about the vertical, degrees. */
    hdriRotation: number;
    /** The HDRI seen through the windows (true), or lighting only with the plain sky colour behind the glass. */
    hdriVisible: boolean;
    /** The ceiling lamps, as a share of what the look sets them to. 0 … 5. */
    lampScale: number;
    /**
     * The view outside: a ground-projected backdrop seen through the windows
     * and doors — a big dome round the house with a ground disc at floor
     * level, painted with an HDRI as seen from `heightFt` up, so the photo's
     * ground meets the floor and its horizon sits where it should. Seen by
     * the camera, through glass and in reflections only: it never lights,
     * shades or shadows anything (the lighting stays the sky or HDRI above).
     */
    view: {
      enabled: boolean;
      /** The backdrop's HDRI (library file name); null: the HDRI lighting the room. */
      image: string | null;
      /** How high the backdrop photo was taken (feet): where its horizon sits. 2 … 20. */
      heightFt: number;
      /** How far out the backdrop dome stands (feet). 40 … 2000. */
      radiusFt: number;
      /** Its turn about the vertical (degrees); null: follows the lighting HDRI's rotation. */
      rotation: number | null;
      /** Its brightness. 0 … 10. */
      strength: number;
    };
  };
  materials: {
    /** Wall&CeilingBoard. `joints`: how much the joints between boards show — 0 hides them (the boards
     *  shade as one flat face), 1 the full profile; null follows the look's seam Strength. */
    wcb: PvcFinish & { joints: number | null };
    slatwall: PvcFinish;
    trim: PvcFinish;
    floor: {
      roughness: number;
      /** How much the floor's picture raises it into relief. 0 … 0.5. */
      relief: number;
    };
    /**
     * Photographic texture sets from the texture library (scripts/blender/textures/<id>/, fetched
     * from Poly Haven or baked from a BlenderKit material), by surface: a placement (the set and
     * how it is laid on), or null for the renderer's own procedural finish. lawn: the yard's grass
     * and its blades; wood: doors and garage doors of the wood styles; fence: the yard's fence;
     * lumber: the 2x4 framing; frame: window and door frames and sills; floor: each room's floor
     * (in place of the view's floor picture); slab: the driveway, apron and landings; drywall: the
     * bare areas' sheets. docs/TEXTURE-STUDIO-SPEC.md.
     */
    textures: TextureChoices;
    /**
     * Placements for particular items, over the surface's: by an opening style's id
     * (openingStyles.ts: 'garage-planks-cedar', 'door-shaker-walnut', 'window-hung-white' …).
     * A wood style's entry dresses that style's doors in place of `textures.wood`; any style's
     * entry dresses its frames and sills in place of `textures.frame` (the renderer resolves
     * the item by the mesh's style tag, then the surface). Absent: the surface's placement.
     */
    items: TextureItems;
  };
  effects: {
    /** Stops added to the designer's exposure. −4 … 4. */
    exposure: number;
    view: ViewTransform;
    /** AgX only. */
    contrast: ContrastLook;
    /** 0 grey … 1 as rendered … 2 vivid. */
    saturation: number;
    /** Contact shading — the darkening where surfaces meet — as a share of the renderer's own. 0 … 2. */
    contact: number;
    /** A glow round the brightest highlights (lamps, sun patches). 0 off … 1. */
    bloom: number;
    /** Darkened corners, as a lens gives. 0 off … 1. */
    vignette: number;
    /** Colour fringes at the frame's edges (chromatic aberration). 0 off … 1. */
    dispersion: number;
  };
}

const PVC: PvcFinish = { roughness: 0.3, specular: 0.5, coat: 0.25, coatRoughness: 0.12 };

/** What the renderer did before there were settings: the defaults, and "Reset" in the window. */
/** The surfaces a photographic texture set can be chosen for. */
export type TextureSurface = 'lawn' | 'wood' | 'fence' | 'lumber' | 'frame' | 'floor' | 'slab' | 'drywall';
export const TEXTURE_SURFACES: TextureSurface[] = ['lawn', 'wood', 'fence', 'lumber', 'frame', 'floor', 'slab', 'drywall'];
/**
 * A texture set on a surface: which set, and how it is laid on. The set tiles at its real size
 * times `scale`, turned `rotation` degrees on the surface; `roughness` and `relief` multiply the
 * set's roughness and bump; `brightness` multiplies its colour. All 1 (0 for rotation): the set as
 * published, which is what a bare set id meant before placements existed.
 */
export interface TexturePlacement {
  id: string;
  /** 0.1 … 10: the tile this many times its real size. */
  scale: number;
  /** −180 … 180 °: the picture turned on the surface (90 runs a grain across instead of along). */
  rotation: number;
  /** 0 … 2: times the set's roughness. */
  roughness: number;
  /** 0 … 2: times the bump its normal and displacement make; 0 is flat. */
  relief: number;
  /** 0.5 … 1.5: times its colour. */
  brightness: number;
}
export const PLACEMENT_FACTORY: Omit<TexturePlacement, 'id'> = { scale: 1, rotation: 0, roughness: 1, relief: 1, brightness: 1 };
/** An item's key: an opening style id. */
export const ITEM_KEY = /^[a-z0-9-]{1,60}$/;
export type TextureItems = Record<string, TexturePlacement>;
export type TextureChoices = Record<TextureSurface, TexturePlacement | null>;
export const NO_TEXTURES: TextureChoices = { lawn: null, wood: null, fence: null, lumber: null, frame: null, floor: null, slab: null, drywall: null };

export const FACTORY_PHOTO_RENDER: PhotoRenderSettings = {
  version: PHOTO_RENDER_VERSION,
  output: { samples: 256, denoise: true, width: 1600, aspect: 'view', views: 'all', panoWidth: 8192 },
  camera: { focalMm: null, lensType: 'perspective', dof: false, fStop: 2.8, focusFt: null, bokehBlades: 0, bokehRotation: 0, bokehRatio: 1 },
  light: {
    sun: 'auto', sunStrength: 3.5, sunElevation: 38, sunSwing: 30, sunSoftness: 1,
    world: 'sky', skyStrength: 0.6, hdri: null, hdriStrength: 1, hdriRotation: 0, hdriVisible: true,
    lampScale: 1,
    view: { enabled: false, image: null, heightFt: 5.5, radiusFt: 300, rotation: null, strength: 1 },
  },
  materials: {
    wcb: { ...PVC, joints: null },
    slatwall: { ...PVC },
    trim: { ...PVC, roughness: 0.28 },
    floor: { roughness: 0.68, relief: 0.05 },
    textures: { ...NO_TEXTURES },
    items: {},
  },
  effects: { exposure: 0, view: 'AgX', contrast: 'Base', saturation: 1, contact: 1, bloom: 0, vignette: 0, dispersion: 0 },
};

export const CONTRAST_LOOKS: ContrastLook[] = ['Very Low', 'Low', 'Medium Low', 'Base', 'Medium High', 'High', 'Very High', 'Punchy'];
export const VIEW_TRANSFORMS: ViewTransform[] = ['AgX', 'Filmic', 'Standard'];

/** Each number's range: what the window offers and what is accepted from a file. */
export const RANGES = {
  'output.samples': [16, 4096],
  'output.width': [640, 7680],
  'camera.focalMm': [8, 300],
  'camera.fStop': [0.95, 22],
  'camera.focusFt': [0.5, 200],
  'camera.bokehBlades': [0, 12],
  'camera.bokehRotation': [-180, 180],
  'camera.bokehRatio': [0.5, 2],
  'light.sunStrength': [0, 20],
  'light.sunElevation': [2, 89],
  'light.sunSwing': [-90, 90],
  'light.sunSoftness': [0, 15],
  'light.skyStrength': [0, 5],
  'light.hdriStrength': [0, 10],
  'light.hdriRotation': [-180, 180],
  'light.lampScale': [0, 5],
  'light.view.heightFt': [2, 20],
  'light.view.radiusFt': [40, 2000],
  'light.view.rotation': [-180, 180],
  'light.view.strength': [0, 10],
  'finish.roughness': [0, 1],
  'finish.specular': [0, 1],
  'finish.coat': [0, 1],
  'finish.coatRoughness': [0, 1],
  'materials.wcb.joints': [0, 1],
  'materials.floor.roughness': [0, 1],
  'materials.floor.relief': [0, 0.5],
  'textures.scale': [0.1, 10],
  'textures.rotation': [-180, 180],
  'textures.roughness': [0, 2],
  'textures.relief': [0, 2],
  'textures.brightness': [0.5, 1.5],
  'effects.exposure': [-4, 4],
  'effects.saturation': [0, 2],
  'effects.contact': [0, 2],
  'effects.bloom': [0, 1],
  'effects.vignette': [0, 1],
  'effects.dispersion': [0, 1],
} as const;
export type RangeKey = keyof typeof RANGES;

const clamp = (v: number, [lo, hi]: readonly [number, number]) => Math.min(hi, Math.max(lo, v));

/** A number from a file, in range, else the fallback. */
function num(v: unknown, key: RangeKey, fallback: number, whole = false): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  const n = clamp(v, RANGES[key]);
  return whole ? Math.round(n) : n;
}
const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);
function pick<T extends string>(v: unknown, options: readonly T[], fallback: T): T {
  return typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : fallback;
}
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** An HDRI file name the library accepts: one plain name, .hdr or .exr. */
export const HDRI_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,80}\.(hdr|exr)$/;
/** A texture set's id in the texture library (a Poly Haven asset id): a folder name, lower case. */
export const TEXTURE_ID = /^[a-z0-9_]{1,80}$/;

/**
 * A placement as given: a bare set id (how a choice was saved before placements) is the set as
 * published; an object has each number pulled into range, missing ones the factory value. Null for
 * anything that names no valid set.
 */
export function texturePlacement(raw: unknown): TexturePlacement | null {
  if (typeof raw === 'string') return TEXTURE_ID.test(raw) ? { id: raw, ...PLACEMENT_FACTORY } : null;
  const r = obj(raw);
  if (typeof r.id !== 'string' || !TEXTURE_ID.test(r.id)) return null;
  return {
    id: r.id,
    scale: num(r.scale, 'textures.scale', PLACEMENT_FACTORY.scale),
    rotation: num(r.rotation, 'textures.rotation', PLACEMENT_FACTORY.rotation),
    roughness: num(r.roughness, 'textures.roughness', PLACEMENT_FACTORY.roughness),
    relief: num(r.relief, 'textures.relief', PLACEMENT_FACTORY.relief),
    brightness: num(r.brightness, 'textures.brightness', PLACEMENT_FACTORY.brightness),
  };
}

/** The items' placements as given: each a valid key with a placement; anything else left out. */
export function textureItems(raw: unknown): TextureItems {
  const out: TextureItems = {};
  for (const [key, value] of Object.entries(obj(raw))) {
    if (!ITEM_KEY.test(key)) continue;
    const p = texturePlacement(value);
    if (p) out[key] = p;
  }
  return out;
}

/** The texture choices as given, a placement or null per surface. `floorScale`: an older file's materials.floor.scale, taken into the floor's placement. */
function textureChoices(raw: unknown, floorScale?: unknown): TextureChoices {
  const t = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = { ...NO_TEXTURES };
  for (const k of TEXTURE_SURFACES) out[k] = texturePlacement(t[k]);
  if (out.floor && typeof t.floor === 'string' && typeof floorScale === 'number' && Number.isFinite(floorScale)) {
    out.floor = { ...out.floor, scale: num(floorScale, 'textures.scale', 1) };
  }
  return out;
}

function finish(raw: unknown, base: PvcFinish): PvcFinish {
  const r = obj(raw);
  return {
    roughness: num(r.roughness, 'finish.roughness', base.roughness),
    specular: num(r.specular, 'finish.specular', base.specular),
    coat: num(r.coat, 'finish.coat', base.coat),
    coatRoughness: num(r.coatRoughness, 'finish.coatRoughness', base.coatRoughness),
  };
}

/**
 * Settings from anywhere (browser storage, a file someone saved, an older
 * version): every field checked, out-of-range numbers pulled into range,
 * anything missing or malformed the factory value. Never throws.
 */
export function normalizePhotoRender(raw: unknown): PhotoRenderSettings {
  const F = FACTORY_PHOTO_RENDER;
  const r = obj(raw);
  const o = obj(r.output), c = obj(r.camera), l = obj(r.light), m = obj(r.materials), e = obj(r.effects);
  const wcb = obj(m.wcb);
  const floor = obj(m.floor);
  const hdri = typeof l.hdri === 'string' && HDRI_NAME.test(l.hdri) ? l.hdri : null;
  return {
    version: PHOTO_RENDER_VERSION,
    output: {
      samples: num(o.samples, 'output.samples', F.output.samples, true),
      denoise: bool(o.denoise, F.output.denoise),
      width: num(o.width, 'output.width', F.output.width, true),
      aspect: pick(o.aspect, OUTPUT_ASPECTS, F.output.aspect),
      views: pick(o.views, VIEW_SETS, F.output.views),
      panoWidth: (PANO_WIDTHS as readonly number[]).includes(o.panoWidth as number) ? (o.panoWidth as (typeof PANO_WIDTHS)[number]) : F.output.panoWidth,
    },
    camera: {
      focalMm: c.focalMm === null || c.focalMm === undefined ? null : num(c.focalMm, 'camera.focalMm', 50),
      lensType: pick(c.lensType, LENS_TYPES, F.camera.lensType),
      dof: bool(c.dof, F.camera.dof),
      fStop: num(c.fStop, 'camera.fStop', F.camera.fStop),
      focusFt: c.focusFt === null || c.focusFt === undefined ? null : num(c.focusFt, 'camera.focusFt', 10),
      bokehBlades: (() => { const b = num(c.bokehBlades, 'camera.bokehBlades', F.camera.bokehBlades, true); return b > 0 && b < 3 ? 3 : b; })(),
      bokehRotation: num(c.bokehRotation, 'camera.bokehRotation', F.camera.bokehRotation),
      bokehRatio: num(c.bokehRatio, 'camera.bokehRatio', F.camera.bokehRatio),
    },
    light: {
      sun: pick(l.sun, ['auto', 'on', 'off'] as const, F.light.sun),
      sunStrength: num(l.sunStrength, 'light.sunStrength', F.light.sunStrength),
      sunElevation: num(l.sunElevation, 'light.sunElevation', F.light.sunElevation),
      sunSwing: num(l.sunSwing, 'light.sunSwing', F.light.sunSwing),
      sunSoftness: num(l.sunSoftness, 'light.sunSoftness', F.light.sunSoftness),
      world: hdri ? pick(l.world, ['sky', 'hdri'] as const, F.light.world) : 'sky',
      skyStrength: num(l.skyStrength, 'light.skyStrength', F.light.skyStrength),
      hdri,
      hdriStrength: num(l.hdriStrength, 'light.hdriStrength', F.light.hdriStrength),
      hdriRotation: num(l.hdriRotation, 'light.hdriRotation', F.light.hdriRotation),
      hdriVisible: bool(l.hdriVisible, F.light.hdriVisible),
      lampScale: num(l.lampScale, 'light.lampScale', F.light.lampScale),
      view: (() => {
        const v = obj(l.view);
        return {
          enabled: bool(v.enabled, F.light.view.enabled),
          image: typeof v.image === 'string' && HDRI_NAME.test(v.image) ? v.image : null,
          heightFt: num(v.heightFt, 'light.view.heightFt', F.light.view.heightFt),
          radiusFt: num(v.radiusFt, 'light.view.radiusFt', F.light.view.radiusFt),
          rotation: v.rotation === null || v.rotation === undefined ? null : num(v.rotation, 'light.view.rotation', 0),
          strength: num(v.strength, 'light.view.strength', F.light.view.strength),
        };
      })(),
    },
    materials: {
      wcb: { ...finish(wcb, F.materials.wcb), joints: wcb.joints === null || wcb.joints === undefined ? null : num(wcb.joints, 'materials.wcb.joints', 0.3) },
      slatwall: finish(m.slatwall, F.materials.slatwall),
      trim: finish(m.trim, F.materials.trim),
      floor: {
        roughness: num(floor.roughness, 'materials.floor.roughness', F.materials.floor.roughness),
        relief: num(floor.relief, 'materials.floor.relief', F.materials.floor.relief),
      },
      textures: textureChoices(m.textures, floor.scale),
      items: textureItems(m.items),
    },
    effects: {
      exposure: num(e.exposure, 'effects.exposure', F.effects.exposure),
      view: pick(e.view, VIEW_TRANSFORMS, F.effects.view),
      contrast: pick(e.contrast, CONTRAST_LOOKS, F.effects.contrast),
      saturation: num(e.saturation, 'effects.saturation', F.effects.saturation),
      contact: num(e.contact, 'effects.contact', F.effects.contact),
      bloom: num(e.bloom, 'effects.bloom', F.effects.bloom),
      vignette: num(e.vignette, 'effects.vignette', F.effects.vignette),
      dispersion: num(e.dispersion, 'effects.dispersion', F.effects.dispersion),
    },
  };
}

/** The picture's width ÷ height: the chosen ratio, or the designer's view's (`viewAspect`). */
export function outputAspectRatio(aspect: OutputAspect, viewAspect: number): number {
  if (aspect === 'view') return viewAspect;
  const [w, h] = aspect.split(':').map(Number);
  return w / h;
}

/** The picture's size in pixels for these settings, given the designer's view's shape. */
export function outputSize(s: PhotoRenderSettings, viewAspect: number): [number, number] {
  return [s.output.width, Math.round(s.output.width / outputAspectRatio(s.output.aspect, viewAspect))];
}

// ── Config files: every setting, saved to and read from a .json file ─────────

/** What marks a saved config file (so a stray JSON file isn't taken for one). */
export const PHOTO_RENDER_FILE_KIND = 'trusscore-photoreal-render-settings';

/** The settings as a config file's text. */
export function photoRenderToFile(s: PhotoRenderSettings): string {
  return JSON.stringify({ kind: PHOTO_RENDER_FILE_KIND, settings: s }, null, 2);
}

/**
 * Settings from a config file's text: a file saved by Export Config, a room
 * export's `room.render.json` (its `render`), or the bare settings object.
 * Every setting is then set from it — anything it lacks or gets wrong is the
 * factory value (normalizePhotoRender). Throws when the text is not one of those.
 */
export function photoRenderFromFile(text: string): PhotoRenderSettings {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('not-json');
  }
  const r = obj(raw);
  const body = r.kind === PHOTO_RENDER_FILE_KIND ? obj(r.settings) : r.render !== undefined && r.camera !== undefined ? obj(r.render) : r;
  const sections = ['output', 'camera', 'light', 'materials', 'effects'];
  if (!sections.some((k) => body[k] && typeof body[k] === 'object')) throw new Error('not-settings');
  return normalizePhotoRender(body);
}

/** Where the window keeps its settings between visits (this browser only). */
export const PHOTO_RENDER_STORAGE_KEY = 'trusscore.photoRender.v1';

export function loadPhotoRender(): PhotoRenderSettings {
  try {
    const text = window.localStorage.getItem(PHOTO_RENDER_STORAGE_KEY);
    return text ? normalizePhotoRender(JSON.parse(text)) : FACTORY_PHOTO_RENDER;
  } catch {
    return FACTORY_PHOTO_RENDER;
  }
}

export function savePhotoRender(s: PhotoRenderSettings): void {
  try {
    window.localStorage.setItem(PHOTO_RENDER_STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Private window or storage blocked: the settings still go with this export.
  }
}
